import { track } from "./track";
import { nameChart, readWorkflow } from "./clickWatcher";
import { componentId } from "./componentId";

const BUSY_REDRAWS = 30;
const BUSY_WINDOW_MS = 60000;
const EMPTY_AFTER_MS = 3000;
const MAX_TEXT = 60;
const AUTO_NAME_ATTRIBUTE = "data-track-chart-auto";
export const FALLBACK_ATTRIBUTE = "data-track-chart-fallback";
const MARK = "__animoTracking";

const clean = (text) =>
  String(text || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_TEXT);

const chartType = (chart) => {
  const fromOptions = chart.options && chart.options.chart && chart.options.chart.type;
  const fromSeries = chart.series && chart.series[0] && chart.series[0].type;
  return fromOptions || fromSeries || "line";
};

const pointCount = (chart) =>
  (chart.series || []).reduce((sum, series) => {
    const count = (series.xData && series.xData.length) || (series.points && series.points.length) || (series.data && series.data.length) || 0;
    return sum + count;
  }, 0);

const titleOf = (chart) => clean((chart.title && chart.title.textStr) || (chart.options && chart.options.title && chart.options.title.text));

const elementOf = (chart) => chart.renderTo || chart.container || null;

const stampTitle = (chart) => {
  const element = elementOf(chart);
  const title = titleOf(chart);
  if (!element || !title || !element.setAttribute) return;
  const alreadyNamed = element.closest && element.closest("[data-track-chart]:not([" + AUTO_NAME_ATTRIBUTE + "])");
  if (alreadyNamed) return;
  if (element.getAttribute("data-track-chart") !== title) {
    element.setAttribute("data-track-chart", title);
    element.setAttribute(AUTO_NAME_ATTRIBUTE, "yes");
  }
};

const describe = (chart) => {
  const element = elementOf(chart);
  const named = element && element.closest ? nameChart(element, ".highcharts-title") : { name: "unnamed", named: false };
  if (named.named) return named;
  const title = titleOf(chart);
  if (title) return { name: title, named: true };
  if (named.name && named.name !== "unnamed") return named;
  const fallback = element && element.getAttribute ? element.getAttribute(FALLBACK_ATTRIBUTE) : null;
  return { name: fallback || `${chartType(chart)} chart`, named: false };
};

const stampFallback = (chart) => {
  const element = elementOf(chart);
  if (!element || !element.setAttribute || titleOf(chart) || element.getAttribute(FALLBACK_ATTRIBUTE)) return;
  const named = element.closest ? nameChart(element, ".highcharts-title") : { name: "unnamed", named: false };
  if (named.named || (named.name && named.name !== "unnamed")) return;
  const base = `${chartType(chart)} chart`;
  const taken = Array.from(document.querySelectorAll(`[${FALLBACK_ATTRIBUTE}]`)).map((item) => item.getAttribute(FALLBACK_ATTRIBUTE));
  let name = base;
  for (let number = 2; taken.includes(name); number += 1) name = `${base} ${number}`;
  element.setAttribute(FALLBACK_ATTRIBUTE, name);
};

const extraFor = (chart, named) => ({
  unnamed: !named.named || undefined,
  component_id: componentId(chart.container || elementOf(chart), "chart", named.name, named.named),
  ...readWorkflow(elementOf(chart)),
});

const emit = (chart, action, value, detail) => {
  const named = describe(chart);
  track("chart", named.name, action, value, { ...extraFor(chart, named), ...(detail ? { detail } : {}) });
};

const shape = (chart) => {
  const series = (chart.series || []).length;
  const points = pointCount(chart);
  return { chartType: chartType(chart), series, points };
};

const formatBound = (axis, value) => {
  if (value === undefined || value === null || Number.isNaN(value)) return "?";
  if (axis && axis.options && axis.options.type === "datetime") return new Date(value).toISOString().slice(0, 16).replace("T", " ");
  return Math.abs(value) >= 100 ? String(Math.round(value)) : String(Math.round(value * 100) / 100);
};

const rangeOf = (event) => {
  const axis = event.xAxis && event.xAxis[0];
  if (!axis) return undefined;
  return `${formatBound(axis.axis, axis.min)} – ${formatBound(axis.axis, axis.max)}`;
};

const nameSpaces = (Highcharts) => (Array.isArray(Highcharts) ? Highcharts : [Highcharts]).filter((item) => item && item.Chart && item.addEvent);

export const watchHighcharts = (Highcharts, { now = Date.now } = {}) => {
  const state = new WeakMap();
  const removers = [];

  const stateOf = (chart) => {
    if (!state.has(chart)) state.set(chart, { loadedAt: null, reported: false, redraws: [], busy: false, errors: new Set() });
    return state.get(chart);
  };

  const reportLoad = (chart, at) => {
    const item = stateOf(chart);
    const current = shape(chart);
    if (!current.points) return;
    item.reported = true;
    stampTitle(chart);
    emit(chart, "load", String(current.points), { chart_type: current.chartType, series: current.series, points: current.points, after_ms: item.loadedAt === null ? 0 : at - item.loadedAt });
  };

  const onLoad = function onLoad() {
    const chart = this;
    const item = stateOf(chart);
    item.loadedAt = now();
    stampTitle(chart);
    stampFallback(chart);
    reportLoad(chart, item.loadedAt);
  };

  const onRedraw = function onRedraw() {
    const chart = this;
    const item = stateOf(chart);
    const at = now();
    stampTitle(chart);
    if (!item.reported) reportLoad(chart, at);
    item.redraws = item.redraws.filter((time) => at - time < BUSY_WINDOW_MS);
    item.redraws.push(at);
    if (!item.busy && item.redraws.length >= BUSY_REDRAWS) {
      item.busy = true;
      emit(chart, "busy", String(item.redraws.length));
    }
  };

  const onDestroy = function onDestroy() {
    const chart = this;
    const item = stateOf(chart);
    const at = now();
    const onScreenMs = item.loadedAt === null ? 0 : at - item.loadedAt;
    if (!item.reported && onScreenMs >= EMPTY_AFTER_MS) emit(chart, "empty", undefined, { chart_type: chartType(chart) });
    if (item.reported) emit(chart, "close", String(Math.round(onScreenMs / 1000)), { on_screen_ms: onScreenMs, redraws: item.redraws.length });
    state.delete(chart);
  };

  const onSelection = function onSelection(event) {
    const chart = this;
    if (event && event.resetSelection) emit(chart, "zoomReset");
    else emit(chart, "zoom", rangeOf(event || {}));
  };

  const onError = (event) => {
    if (!event || typeof event.code !== "number") return;
    const chart = event.chart;
    const message = clean(event.message || `Highcharts error #${event.code}`);
    if (!chart) {
      track("chart", "unnamed", "error", message, { unnamed: true });
      return;
    }
    const item = stateOf(chart);
    if (item.errors.has(message)) return;
    item.errors.add(message);
    emit(chart, "error", message, { code: event.code });
  };

  nameSpaces(Highcharts).forEach((space) => {
    if (space[MARK]) return;
    space[MARK] = true;
    removers.push(space.addEvent(space.Chart, "load", onLoad));
    removers.push(space.addEvent(space.Chart, "redraw", onRedraw));
    removers.push(space.addEvent(space.Chart, "destroy", onDestroy));
    removers.push(space.addEvent(space.Chart, "selection", onSelection));
    removers.push(space.addEvent(space, "displayError", onError));
    removers.push(() => {
      delete space[MARK];
    });
  });

  return () => removers.forEach((remove) => typeof remove === "function" && remove());
};

export const watchHighchartsWhenLoaded = () => {
  let stop = () => {};
  let cancelled = false;
  Promise.all([import("highcharts"), import("highcharts/highstock")])
    .then((modules) => {
      if (cancelled) return;
      stop = watchHighcharts(modules.map((module) => module.default || module));
    })
    .catch(() => {});
  return () => {
    cancelled = true;
    stop();
  };
};

export default watchHighcharts;
